import type { ComponentPropsWithRef, ReactNode } from 'react';
import { Building2, UserCog } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { Button } from '../button/button.tsx';

export interface ImpersonationBannerProps extends Omit<
  ComponentPropsWithRef<'div'>,
  'style' | 'children'
> {
  /* `loginAs`: somebody else's account. `visit`: the superadmin's own guest account in a customer organisation. */
  kind: 'loginAs' | 'visit';
  /* What is going on, in one sentence — who this session is being seen as. */
  children: ReactNode;
  /* The way out, right where the state is announced. Both or neither. */
  stopLabel?: string;
  onStop?: () => void;
}

const KINDS = {
  loginAs: {
    Icon: UserCog,
    bar: 'bg-danger text-on-danger',
    stop: 'onDanger',
  },
  visit: {
    Icon: Building2,
    bar: 'bg-primary text-white',
    stop: 'onPrimary',
  },
} as const;

/*
 * Not dismissable and carries the way out: the danger is forgetting you are in
 * it. Red for another person's account, navy for your own guest account. One
 * fixed line, because the shell subtracts exactly `--app-banner-h` below it.
 */
export function ImpersonationBanner({
  kind,
  className,
  children,
  stopLabel,
  onStop,
  ...props
}: ImpersonationBannerProps) {
  const { Icon, bar, stop } = KINDS[kind];

  return (
    <div
      data-slot='impersonation-banner'
      data-kind={kind}
      /* Not `alert`: nothing has gone wrong and nothing waits on the user. */
      role='status'
      className={cn(
        'flex h-12 items-center justify-center gap-3 px-5 text-sm font-semibold',
        bar,
        className,
      )}
      {...props}
    >
      <Icon aria-hidden='true' className='size-4 shrink-0' />
      <span className='truncate'>{children}</span>
      {stopLabel && onStop ? (
        <Button
          variant={stop}
          size='sm'
          shape='pill'
          className='shrink-0'
          onClick={onStop}
        >
          {stopLabel}
        </Button>
      ) : null}
    </div>
  );
}
